import React, { useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router";

import { capitalize } from "lodash";
import invariant from "tiny-invariant";

import Loading from "../../../../components/Loading";
import { alertActions } from "../../../../features/alert/alertSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import {
    confirmPlaceholderSchedule,
    unconfirmUmpireSchedule,
} from "../../../../lib/api";
import { IMasterTournament } from "../../../../types/api";
import { AsyncStatuses } from "../../../../types/shared";

interface ModalProps {
    show: "confirm" | "unconfirm" | undefined;
    setShow: React.Dispatch<
        React.SetStateAction<"confirm" | "unconfirm" | undefined>
    >;
}

const ConfirmScheduleModal = ({ show, setShow }: ModalProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [asyncStatus, setAsyncStatus] = useState<AsyncStatuses>();
    const tournament = useAppSelector(
        selectSelectedTournament,
    ) as IMasterTournament;

    const handleSubmit = async (type: "confirm" | "unconfirm") => {
        setAsyncStatus("pending");
        try {
            invariant(tournament);
            const response =
                type === "confirm"
                    ? await confirmPlaceholderSchedule(tournament["id"])
                    : await unconfirmUmpireSchedule(tournament["id"]);

            // need to update master tournaments here to update settings in redux
            dispatch(tournamentManagementActions.getMasterTournaments());
            dispatch(
                alertActions.success({
                    title: `Umpire schedule has been ${type}ed`,
                    dismissible: true,
                    persist: true,
                }),
            );
            setAsyncStatus("success");

            if (type === "confirm") {
                navigate(
                    `/tournament-management/tournament/${tournament["id"]}/calendar`,
                );
            }
        } catch (error: any) {
            dispatch(
                alertActions.success({
                    title: `There was a problem ${type}ing the umpire schedule`,
                    text: error,
                }),
            );
            setAsyncStatus("failed");
        } finally {
            setShow(undefined);
        }
    };

    return (
        <Modal
            show={show !== undefined}
            onHide={() => setShow(undefined)}
            centered
        >
            <Modal.Header>
                <Modal.Title>{capitalize(show)} Umpire Schedule?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row>
                        <Col>
                            {show && show === "confirm" ? (
                                <React.Fragment>
                                    Are you sure you wish to confirm the umpire
                                    schedule? This will "match up" all
                                    placeholders with the games at the
                                    respective time and field. Please note that
                                    the games must match up on the calendar
                                    exactly for it to be confirmed (ie, no
                                    overlaps, matching start and end time, etc).
                                    <br />
                                    All "shaded" games on the calendar are
                                    matched up with a placeholder. Any
                                    semi-transparent placeholder is not
                                    currently matched up with a game and
                                    therefore will not be "confirmed" on the
                                    schedule.
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    Are you sure you wish to <b>unconfirm</b>{" "}
                                    the umpire schedule? This will remove all
                                    assigned umpires from all unplayed
                                    tournament games and revert back to using
                                    placeholders for umpire scheduling. After
                                    making any changes, the umpire schedule will
                                    need to be confirmed again to assign the
                                    umpires back to the games.
                                </React.Fragment>
                            )}

                            {asyncStatus === "pending" && (
                                <Loading className="text-center" />
                            )}
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={() => setShow(undefined)}
                    disabled={asyncStatus === "pending"}
                >
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={() => show !== undefined && handleSubmit(show)}
                    disabled={asyncStatus === "pending"}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmScheduleModal;
